import { z } from "zod/v4";

import { BundleDiscountDescriptions } from "../descriptions/component-dto.describe";
import { assertDescriptions } from "../descriptions/types";

import {
  type ActiveUpsellDiscountType,
  ActiveUpsellDiscountTypeList,
  UpsellCompareAtPriceDisplayList,
  type UpsellDiscountType,
  UpsellDiscountTypeList,
} from "./upsell-product-rules";

// Panel-only: "fixed" is a single tier, so it's derived from `discountTiers`.
export const BundleDiscountStructureList = ["fixed", "tiered"] as const;
export type BundleDiscountStructure =
  (typeof BundleDiscountStructureList)[number];

// A threshold: applies at this count or more. Highest qualifying tier wins and
// tiers never stack — resolve with `resolveBundleTier`.
export const BundleDiscountTierSchema = z.object({
  minimumProducts: z
    .number()
    .int()
    .min(1)
    .describe(BundleDiscountDescriptions.discountTiers),
  value: z.number().describe(BundleDiscountDescriptions.discountTiers),
});
export type BundleDiscountTier = z.infer<typeof BundleDiscountTierSchema>;

export const BundleDiscountSchema = z.object({
  discountType: z
    .enum(UpsellDiscountTypeList)
    .default("none")
    .describe(BundleDiscountDescriptions.discountType),
  discountTiers: z
    .array(BundleDiscountTierSchema)
    .default([])
    .describe(BundleDiscountDescriptions.discountTiers),
  compareAtPriceDisplay: z
    .enum(UpsellCompareAtPriceDisplayList)
    .default("none")
    .describe(BundleDiscountDescriptions.compareAtPriceDisplay),
});
export type BundleDiscount = z.infer<typeof BundleDiscountSchema>;

// What both component shapes reduce to once an inactive discount is ruled out.
// A flat `discountValue` reaches it through `toSingleTier`.
export const ActiveComponentDiscountSchema = z.object({
  discountType: z.enum(ActiveUpsellDiscountTypeList),
  discountTiers: z.array(BundleDiscountTierSchema),
});
export type ActiveComponentDiscount = z.infer<
  typeof ActiveComponentDiscountSchema
>;

assertDescriptions<typeof BundleDiscountDescriptions, BundleDiscount>();
